import { useState, useEffect, useRef } from "react";
import {
  Package, Truck, CheckCircle, Wallet, CreditCard, Banknote,
  MapPin, Clock, Navigation, TrendingUp, Trophy, Zap,
  Award, Target, Star, Flame, ChevronUp, Bell, Shield,
  Info, Map as MapIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DriverLayout } from "@/components/layouts/DriverLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { cn } from "@/lib/utils";

import API from "@/utils/api";

// Almería center coordinates
const ALMERIA_CENTER = [36.8340, -2.4637];

// Custom marker icon for driver
const driverIcon = L.divIcon({
  className: "driver-marker",
  html: `<div style="
    background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 4px 15px rgba(0,0,0,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  ">
    <div style="position: absolute; top: -5px; right: -5px; background: red; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>
    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
  </div>`,
  iconSize: [44, 44],
  iconAnchor: [22, 22],
});

// Custom marker icon for delivery orders
const deliveryIcon = L.divIcon({
  className: "delivery-marker",
  html: `<div style="
    background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    width: 38px;
    height: 38px;
    border-radius: 50%;
    border: 3px solid white;
    box-shadow: 0 3px 12px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    justify-content: center;
  ">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/>
      <circle cx="12" cy="10" r="3"/>
    </svg>
  </div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 38],
  popupAnchor: [0, -38],
});

// Helper component for Gamification Badges
function GamificationBadge({ icon: Icon, label, value, color, delay = "0" }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 p-3 rounded-xl border transition-all hover:scale-105 cursor-default",
        color === "green" ? "bg-green-500/10 border-green-500/20" :
          color === "orange" ? "bg-orange-500/10 border-orange-500/20" :
            color === "blue" ? "bg-blue-500/10 border-blue-500/20" :
              color === "yellow" ? "bg-yellow-500/10 border-yellow-500/20" :
                "bg-gray-500/10 border-gray-500/20"
      )}
      style={{ animation: `fadeIn 0.5s ease-out ${delay}s both` }}
    >
      <div className={cn(
        "w-10 h-10 rounded-lg flex items-center justify-center",
        color === "green" ? "bg-green-500/20 text-green-500" :
          color === "orange" ? "bg-orange-500/20 text-orange-500" :
            color === "blue" ? "bg-blue-500/20 text-blue-500" :
              color === "yellow" ? "bg-yellow-500/20 text-yellow-500" :
                "bg-gray-500/20 text-gray-500"
      )}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-[10px] uppercase font-bold text-gray-400 tracking-wider leading-none mb-1">{label}</p>
        <p className="font-bold text-sm text-white">{value}</p>
      </div>
    </div>
  );
}

// Helper component for Hourly Chart
function HourlyChart({ data }) {
  const hours = Object.keys(data).sort((a, b) => parseInt(a) - parseInt(b));
  const counts = Object.values(data);
  const max = Math.max(...counts, 1);

  return (
    <div className="flex items-end justify-between h-20 gap-1 px-1">
      {Array.from({ length: 12 }).map((_, i) => {
        const hour = (i + 9).toString(); // From 9 AM to 9 PM approx
        const count = data[hour] || 0;
        const height = (count / max) * 100;

        return (
          <div key={hour} className="flex flex-col items-center flex-1">
            <div className="w-full relative group">
              <div
                className={cn(
                  "w-full rounded-t-sm transition-all duration-500",
                  count > 0 ? "bg-yellow-500" : "bg-gray-800"
                )}
                style={{ height: `${Math.max(4, height)}%` }}
              />
              {count > 0 && (
                <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-black text-[10px] px-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {count} ped.
                </div>
              )}
            </div>
            <span className="text-[8px] text-gray-600 mt-1">{hour}h</span>
          </div>
        );
      })}
    </div>
  );
}

export default function DriverDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [isLocating, setIsLocating] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [isAvailable, setIsAvailable] = useState(true);
  const [showHistory, setShowHistory] = useState(false);

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const orderMarkersRef = useRef([]);
  const prevOrderIdsRef = useRef(new Set());  // Track previous order IDs for notifications

  // Sound notification function
  const playNewOrderSound = () => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Create a pleasant notification sound (3 beeps)
      const playBeep = (frequency, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      playBeep(800, now, 0.15);       // First beep
      playBeep(1000, now + 0.2, 0.15); // Second beep (higher)
      playBeep(1200, now + 0.4, 0.2);  // Third beep (highest, longer)
      
      console.log('🔔 Sonido de notificación reproducido');
    } catch (error) {
      console.error('Error reproduciendo sonido:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [ordersRes, historyRes] = await Promise.all([
        axios.get(`${API}/driver/orders`, { withCredentials: true }),
        axios.get(`${API}/driver/history`, { withCredentials: true }),
      ]);

      const deliveryOrders = ordersRes.data.filter(o => o.order_type !== "local");
      const deliveryHistory = historyRes.data.filter(o => o.order_type !== "local");

      // Check for new orders (not in previous set)
      const currentOrderIds = new Set(deliveryOrders.map(o => o.order_id));
      const newOrders = deliveryOrders.filter(o => !prevOrderIdsRef.current.has(o.order_id));
      
      // Play sound if there are new orders and this is not the first load
      if (newOrders.length > 0 && prevOrderIdsRef.current.size > 0) {
        playNewOrderSound();
        console.log(`🆕 ${newOrders.length} nuevo(s) pedido(s) detectado(s):`, newOrders.map(o => o.order_id));
      }
      
      // Update previous order IDs
      prevOrderIdsRef.current = currentOrderIds;

      setOrders(deliveryOrders);
      setHistory(deliveryHistory);
    } catch (error) {
      console.error("Error fetching driver data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize map
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const tryInitMap = () => {
      attempts++;
      if (mapInstanceRef.current || !mapRef.current) return !!mapInstanceRef.current;

      try {
        const map = L.map(mapRef.current, {
          zoomControl: false,
          attributionControl: false,
          preferCanvas: true,
        }).setView(ALMERIA_CENTER, 14);

        L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
          attribution: '&copy; OpenStreetMap &copy; CARTO',
          subdomains: 'abcd',
          maxZoom: 20
        }).addTo(map);

        mapInstanceRef.current = map;
        setMapReady(true);

        [100, 300, 600, 1000].forEach((delay) => {
          setTimeout(() => mapInstanceRef.current?.invalidateSize(), delay);
        });

        return true;
      } catch (error) {
        console.error("❌ Map Init Error:", error);
        return false;
      }
    };

    if (!tryInitMap()) {
      const interval = setInterval(() => {
        if (tryInitMap() || attempts >= maxAttempts) clearInterval(interval);
      }, 200);
      return () => clearInterval(interval);
    }

    return () => {
      mapInstanceRef.current?.remove();
      mapInstanceRef.current = null;
      setMapReady(false);
    };
  }, []);

  // Watch geolocation
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationError("No soportado");
      setIsLocating(false);
      return;
    }

    const watchId = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy } = position.coords;
        setLocation({ lat: latitude, lng: longitude, accuracy });
        setLocationError(null);
        setIsLocating(false);

        if (mapInstanceRef.current) {
          if (markerRef.current) {
            markerRef.current.setLatLng([latitude, longitude]);
          } else {
            markerRef.current = L.marker([latitude, longitude], { icon: driverIcon })
              .addTo(mapInstanceRef.current)
              .bindPopup(`<div class="p-1 font-bold">Estás aquí</div>`);
          }
          if (!location) mapInstanceRef.current.setView([latitude, longitude], 16);
        }
      },
      (error) => {
        setIsLocating(false);
        console.error("Geo error:", error);
        setLocationError("Error GPS");
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, [location]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  // Update order markers
  useEffect(() => {
    if (!mapInstanceRef.current || !mapReady) return;

    orderMarkersRef.current.forEach(m => m.remove());
    orderMarkersRef.current = [];

    const activeDeliveries = orders.filter(o =>
      o.status === "on_the_way" && o.address?.lat && o.address?.lng
    );

    activeDeliveries.forEach(order => {
      const marker = L.marker([order.address.lat, order.address.lng], {
        icon: deliveryIcon
      }).addTo(mapInstanceRef.current);

      marker.bindPopup(`
        <div class="p-2 min-w-[150px]">
          <div class="font-bold text-sm mb-1">${order.user_name || 'Cliente'}</div>
          <div class="text-xs text-gray-500 mb-2">${order.address.street}</div>
          <button 
            onclick="window.openNavigation(${order.address.lat}, ${order.address.lng})"
            style="width:100%;background:#f59e0b;color:black;border:none;padding:5px;border-radius:4px;font-weight:bold;cursor:pointer;"
          >🚀 NAVEGAR</button>
        </div>
      `);
      orderMarkersRef.current.push(marker);
    });
  }, [orders, mapReady]);

  // Global navigation function
  useEffect(() => {
    window.openNavigation = (lat, lng) => {
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      window.open(url, '_blank');
    };
    return () => { delete window.openNavigation; };
  }, []);

  // Calculations
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const todayDeliveries = history.filter(order => {
    const deliveredDate = new Date(order.delivered_at || order.updated_at);
    return deliveredDate >= todayStart;
  });

  const totalCash = todayDeliveries.filter(o => o.payment_method === "cash" || o.payment_method === "pending" || !o.payment_method).reduce((s, o) => s + (o.total || 0), 0);
  const totalCard = todayDeliveries.filter(o => o.payment_method === "card").reduce((s, o) => s + (o.total || 0), 0);
  const totalEarnings = totalCash + totalCard;

  const avgPerDelivery = todayDeliveries.length > 0 ? totalEarnings / todayDeliveries.length : 0;

  const activeDeliveriesCount = orders.filter(o => o.status === "on_the_way").length;
  const readyOrdersCount = orders.filter(o => o.status === "ready").length;

  // Gamification & Leveling
  const calculateLevel = (totalHistory) => {
    if (totalHistory >= 200) return { name: "Leyenda", color: "text-purple-500", icon: Award, next: null };
    if (totalHistory >= 50) return { name: "Maestro", color: "text-yellow-500", icon: Star, next: 200 };
    if (totalHistory >= 15) return { name: "Experto", color: "text-blue-500", icon: Zap, next: 50 };
    return { name: "Novato", color: "text-green-500", icon: Shield, next: 15 };
  };

  const levelInfo = calculateLevel(history.length);
  const progress = levelInfo.next ? Math.min(100, (history.length / levelInfo.next) * 100) : 100;

  // Hourly Statistics
  const hourlyData = todayDeliveries.reduce((acc, order) => {
    const hour = new Date(order.delivered_at || order.updated_at).getHours();
    acc[hour] = (acc[hour] || 0) + 1;
    return acc;
  }, {});

  const cashToSettle = history.filter(o =>
    o.payment_method === "cash" &&
    o.payment_status === "pending" &&
    o.status === "delivered"
  ).reduce((s, o) => s + (o.total || 0), 0);

  const formatCurrency = (val) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(val);

  if (isLoading) {
    return (
      <DriverLayout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
          <div className="w-16 h-16 border-4 border-yellow-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-yellow-500 font-bold animate-pulse">CARGANDO RUTA...</p>
        </div>
      </DriverLayout>
    );
  }

  return (
    <DriverLayout>
      <div className="pb-24"> {/* Extra padding for mobile bottom nav if any */}

        {/* Top Floating Header */}
        <div className="flex items-center justify-between mb-4 bg-black/40 backdrop-blur-md p-3 rounded-2xl border border-white/5 mx-2">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={user?.picture || "https://api.dicebear.com/7.x/avataaars/svg?seed=driver"}
                alt="Driver"
                className="w-12 h-12 rounded-full border-2 border-yellow-500 shadow-lg"
              />
              <div className={cn(
                "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-black",
                isAvailable ? "bg-green-500" : "bg-red-500"
              )} />
            </div>
            <div>
              <p className="font-bold text-white leading-none mb-1 uppercase italic tracking-tighter">¡Hola, {user?.name?.split(' ')[0]}!</p>
              <div className="flex items-center gap-1">
                <levelInfo.icon className={cn("w-3 h-3", levelInfo.color)} />
                <span className={cn("text-[10px] font-black uppercase tracking-widest", levelInfo.color)}>
                  {levelInfo.name}
                </span>
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10 relative"
            onClick={() => toast.info("No hay notificaciones nuevas")}
          >
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-yellow-500 rounded-full"></span>
          </Button>
        </div>

        {/* Map Explorer Section */}
        <div className="relative h-[65vh] w-full rounded-3xl overflow-hidden mb-6 shadow-2xl border border-white/10 group">
          <div ref={mapRef} className="w-full h-full z-0" />

          {/* Overlay Gradients */}
          <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-black/80 to-transparent pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black/80 to-transparent pointer-events-none" />

          {/* Availability Toggle Floating */}
          <div className="absolute top-4 right-4 z-10">
            <Button
              onClick={() => setIsAvailable(!isAvailable)}
              className={cn(
                "rounded-full px-4 h-10 font-bold transition-all shadow-xl",
                isAvailable ? "bg-green-500 hover:bg-green-600 text-black" : "bg-red-500 hover:bg-red-600 text-white"
              )}
            >
              {isAvailable ? "● DISPONIBLE" : "● FUERA DE SERVICIO"}
            </Button>
          </div>

          {/* Quick Stats Floating Boxes */}
          <div className="absolute bottom-8 left-4 right-4 z-10 flex gap-2">
            {/* Cash to Settle Alert */}
            {cashToSettle > 0 && (
              <div className="flex-1 bg-red-600 rounded-2xl p-3 shadow-2xl animate-pulse">
                <div className="flex items-center justify-between mb-1">
                  <Banknote className="w-4 h-4 text-white" />
                  <Badge className="bg-white/20 text-white border-none text-[9px] px-1 h-4">A LIQUIDAR</Badge>
                </div>
                <p className="text-xl font-black text-white">{formatCurrency(cashToSettle)}</p>
                <p className="text-[9px] uppercase font-bold text-white/80">Entregar</p>
              </div>
            )}

            <div className="flex-1 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-3 shadow-2xl">
              <div className="flex items-center justify-between mb-1">
                <Truck className="w-4 h-4 text-orange-500" />
                <Badge className="bg-orange-500/20 text-orange-500 border-none text-[9px] px-1 h-4">ALTA</Badge>
              </div>
              <p className="text-xl font-black text-white">{activeDeliveriesCount}</p>
              <p className="text-[9px] uppercase font-bold text-gray-400">En Camino</p>
            </div>

            <div className="flex-1 bg-black/80 backdrop-blur-xl border border-white/10 rounded-2xl p-3 shadow-2xl">
              <div className="flex items-center justify-between mb-1">
                <Flame className="w-4 h-4 text-green-500" />
                <Badge className="bg-green-500/20 text-green-500 border-none text-[9px] px-1 h-4">DONE</Badge>
              </div>
              <p className="text-xl font-black text-white">{todayDeliveries.length}</p>
              <p className="text-[9px] uppercase font-bold text-gray-400">Completados</p>
            </div>
          </div>

          {/* Center Map Button */}
          <Button
            size="icon"
            onClick={() => {
              if (location) mapInstanceRef.current?.setView([location.lat, location.lng], 17);
            }}
            className="absolute top-20 right-4 z-10 bg-white/10 backdrop-blur-md hover:bg-white/20 border border-white/10 rounded-full"
          >
            <MapIcon className="w-5 h-5 text-white" />
          </Button>
        </div>

        {/* Premium Stats Grid */}
        <div className="px-4 space-y-4">

          {/* Main Earnings Card */}
          <div className="bg-gradient-to-br from-yellow-500 via-orange-500 to-orange-600 rounded-3xl p-5 shadow-2xl relative overflow-hidden group">
            <div className="absolute top-[-20px] right-[-20px] w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-1000" />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <p className="text-black font-black uppercase italic tracking-tighter text-xl">Ingresos Hoy</p>
                <div className="flex gap-2">
                  <div className="bg-black/20 p-2 rounded-xl backdrop-blur-md">
                    <TrendingUp className="w-5 h-5 text-black" />
                  </div>
                </div>
              </div>

              <div className="flex items-end gap-2 mb-6">
                <p className="text-5xl font-black text-black tracking-tight">{formatCurrency(totalEarnings).split(',')[0]}</p>
                <p className="text-2xl font-black text-black/60 mb-1">{formatCurrency(totalEarnings).split(',')[1]}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-black/10 rounded-2xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Banknote className="w-4 h-4 text-black/70" />
                    <span className="text-[10px] uppercase font-black text-black/60">Efectivo</span>
                  </div>
                  <p className="text-xl font-bold text-black">{formatCurrency(totalCash)}</p>
                </div>
                <div className="bg-black/10 rounded-2xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <CreditCard className="w-4 h-4 text-black/70" />
                    <span className="text-[10px] uppercase font-black text-black/60">Tarjeta</span>
                  </div>
                  <p className="text-xl font-bold text-black">{formatCurrency(totalCard)}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Gamification Grid */}
          <div className="grid grid-cols-2 gap-3">
            <GamificationBadge
              icon={Trophy}
              label="Eficiencia"
              value={`${todayDeliveries.length > 0 ? (todayDeliveries.length / 8).toFixed(1) : 0} pedidos/h`}
              color="yellow"
              delay="0.1"
            />
            <GamificationBadge
              icon={Zap}
              label="Promedio/Viaje"
              value={formatCurrency(avgPerDelivery)}
              color="blue"
              delay="0.2"
            />
            <GamificationBadge
              icon={Target}
              label="A tiempo"
              value="98.5%"
              color="green"
              delay="0.3"
            />
            <GamificationBadge
              icon={Flame}
              label="Racha"
              value={`${Math.min(todayDeliveries.length, 12)} racha 🔥`}
              color="orange"
              delay="0.4"
            />
          </div>

          {/* Hourly Delivery Chart Card */}
          <Card className="bg-gray-900/50 border-white/5 backdrop-blur-sm rounded-3xl overflow-hidden shadow-xl">
            <CardHeader className="pb-2 border-b border-white/5">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-bold flex items-center gap-2 text-gray-300">
                  <TrendingUp className="w-4 h-4 text-yellow-500" />
                  ENTREGAS POR HORA
                </CardTitle>
                <div className="text-[10px] text-gray-500 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Hoy
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              <HourlyChart data={hourlyData} />
            </CardContent>
          </Card>

          {/* Level Progress Section */}
          <div className="bg-gray-900 border border-white/5 rounded-3xl p-5 shadow-xl">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-[10px] font-black uppercase text-gray-500 tracking-widest">Próximo Nivel</p>
                  <p className="text-lg font-black text-white italic tracking-tighter">
                    {levelInfo.next ? levelInfo.name.toUpperCase() : "MÁXIMO NIVEL"}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">{history.length} / {levelInfo.next || '∞'} XP</p>
              </div>
            </div>

            <div className="relative w-full h-3 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(168,85,247,0.5)]"
                style={{ width: `${progress}%` }}
              />
            </div>

            {levelInfo.next && (
              <p className="text-[10px] text-center text-gray-500 mt-2 font-bold uppercase tracking-widest">
                FALTA {levelInfo.next - history.length} ENTREGAS PARA EL SIGUIENTE RANGO
              </p>
            )}
          </div>

          {/* Expandable History Section */}
          <div className="bg-gray-900/40 rounded-3xl border border-white/5 overflow-hidden">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-gray-400" />
                <span className="font-bold text-gray-300">HISTORIAL RECIENTE</span>
              </div>
              <ChevronUp className={cn("w-5 h-5 text-gray-500 transition-transform", !showHistory && "rotate-180")} />
            </button>

            <div className={cn(
              "transition-all duration-300 overflow-hidden",
              showHistory ? "max-h-[500px]" : "max-h-0"
            )}>
              <div className="p-4 pt-0 space-y-2">
                {todayDeliveries.length === 0 ? (
                  <p className="text-center py-10 text-gray-600 text-sm italic">Comienza a entregar para ver tu historial...</p>
                ) : (
                  todayDeliveries.slice(0, 5).map((order, i) => (
                    <div key={order.order_id} className="rounded-2xl bg-white/5 border border-white/5 overflow-hidden">
                      <div
                        className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                        onClick={(e) => {
                          const content = document.getElementById(`history-details-${order.order_id}`);
                          if (content) content.classList.toggle('hidden');
                        }}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center font-black",
                            order.payment_method === 'cash' ? "bg-green-500/20 text-green-500" : "bg-blue-500/20 text-blue-500"
                          )}>
                            #{i + 1}
                          </div>
                          <div>
                            <p className="font-bold text-white text-sm">{order.user_name || 'Cliente'}</p>
                            <p className="text-[10px] text-gray-400 font-mono">ID: {order.order_id.slice(-6).toUpperCase()}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-black text-white">{formatCurrency(order.total)}</p>
                          <p className="text-[10px] text-gray-500 uppercase font-bold tracking-widest">
                            {new Date(order.delivered_at || order.updated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                          <Badge className={cn("text-[9px] h-4 mt-1 border-none", order.payment_method === 'card' ? "bg-blue-500/20 text-blue-500" : "bg-green-500/20 text-green-500")}>
                            {order.payment_method === 'card' ? 'TARJETA' : 'EFECTIVO'}
                          </Badge>
                        </div>
                      </div>

                      {/* Hidden details */}
                      <div id={`history-details-${order.order_id}`} className="hidden bg-black/20 p-3 border-t border-white/5 text-xs text-gray-400">
                        <ul className="space-y-1">
                          {order.items?.map((item, idx) => (
                            <li key={idx} className="flex justify-between">
                              <span>{item.quantity}x {item.name}</span>
                              <span>{(item.quantity * item.price).toFixed(2)}€</span>
                            </li>
                          ))}
                        </ul>
                        <div className="mt-2 pt-2 border-t border-white/5 flex justify-between font-bold text-white">
                          <span>TOTAL</span>
                          <span>{order.total?.toFixed(2)}€</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* CSS Animations */}
        <style dangerouslySetInnerHTML={{
          __html: `
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .driver-marker { transition: all 0.3s ease-out; }
          .city-label { pointer-events: none; }
          .leaflet-popup-content-wrapper { 
            background: #111; 
            color: white; 
            border-radius: 15px; 
            border: 1px solid rgba(255,255,255,0.1); 
          }
          .leaflet-popup-tip { background: #111; }
        `}} />
      </div>
    </DriverLayout >
  );
}
